import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { useProfiles } from '@/hooks/useProfiles';
import { X, Users, Move, Trash2, UserPlus } from 'lucide-react';

interface BulkActionsToolbarProps {
  selectedCount: number;
  onClearSelection: () => void;
  onBulkStageUpdate: (stage: string) => void;
  onBulkOwnerUpdate: (ownerId: string) => void;
  onBulkDelete: () => void;
  stages: Array<{ value: string; label: string; color: string }>;
}

export const BulkActionsToolbar: React.FC<BulkActionsToolbarProps> = ({
  selectedCount,
  onClearSelection,
  onBulkStageUpdate,
  onBulkOwnerUpdate,
  onBulkDelete,
  stages
}) => {
  const { profiles } = useProfiles();
  const [selectedStage, setSelectedStage] = useState<string>('');
  const [selectedOwner, setSelectedOwner] = useState<string>('');

  const handleStageUpdate = () => {
    if (selectedStage) {
      onBulkStageUpdate(selectedStage);
      setSelectedStage('');
    }
  };

  const handleOwnerUpdate = () => {
    if (selectedOwner) {
      onBulkOwnerUpdate(selectedOwner);
      setSelectedOwner('');
    }
  };

  return (
    <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
      <Card className="bg-primary text-primary-foreground shadow-lg border-0">
        <CardContent className="flex items-center gap-4 p-4">
          {/* Selection Count */}
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="bg-primary-foreground/20 text-primary-foreground">
              {selectedCount} geselecteerd
            </Badge>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearSelection}
              className="h-8 w-8 p-0 text-primary-foreground hover:bg-primary-foreground/20"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Stage Update */}
          <div className="flex items-center gap-2">
            <Select value={selectedStage} onValueChange={setSelectedStage}>
              <SelectTrigger className="w-48 bg-primary-foreground text-foreground">
                <div className="flex items-center gap-2">
                  <Move className="h-4 w-4" />
                  <SelectValue placeholder="Wijzig status" />
                </div>
              </SelectTrigger>
              <SelectContent>
                {stages.map(stage => (
                  <SelectItem key={stage.value} value={stage.value}>
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${stage.color}`}></div>
                      {stage.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              onClick={handleStageUpdate} 
              disabled={!selectedStage}
              size="sm"
              variant="secondary"
            >
              Update
            </Button>
          </div>

          {/* Owner Assignment */}
          <div className="flex items-center gap-2">
            <Select value={selectedOwner} onValueChange={setSelectedOwner}>
              <SelectTrigger className="w-48 bg-primary-foreground text-foreground">
                <div className="flex items-center gap-2">
                  <UserPlus className="h-4 w-4" />
                  <SelectValue placeholder="Wijs toe aan" />
                </div>
              </SelectTrigger>
              <SelectContent>
                {profiles.map(profile => (
                  <SelectItem key={profile.id} value={profile.id}>
                    {profile.display_name || profile.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              onClick={handleOwnerUpdate} 
              disabled={!selectedOwner}
              size="sm"
              variant="secondary"
            >
              Toewijzen
            </Button>
          </div>

          {/* Delete Action */}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button 
                variant="destructive" 
                size="sm"
                className="bg-destructive hover:bg-destructive/90"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Verwijderen
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Leads verwijderen</AlertDialogTitle>
                <AlertDialogDescription>
                  Weet je zeker dat je {selectedCount} lead{selectedCount > 1 ? 's' : ''} wilt verwijderen? 
                  Deze actie kan niet ongedaan worden gemaakt.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annuleren</AlertDialogCancel>
                <AlertDialogAction onClick={onBulkDelete} className="bg-destructive hover:bg-destructive/90">
                  Verwijderen
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardContent>
      </Card>
    </div>
  );
};